import { Group } from '@visx/group'
import { ParentSize } from '@visx/responsive'
import { Arc } from '@visx/shape'
import cx from 'classnames'
import React from 'react'

import * as tailwindConfig from '../../tailwind.config'

type Props = {
  /** Current progress. */
  progress: number
  /** Total progress for which the task is considered completed. */
  total: number
  /** Color used for the segment showing the currently completed progress. */
  progressColor: string
  /** Color used for the segment showing the ramaining progress. */
  remainingColor: string
  /** Color used for the background. */
  backgroundColor: string
  /** Width of the circle stroke. */
  strokeWidth: number
}

/**
 * Taken from https://github.com/joaquindk/upload-spinner
 *
 * Component used to show the progress of a task. Given a 'progress' count
 * and a 'total' count, it will calculate the progress as a percentage and show a spinner
 * made up of 2 segments:
 *  1. A segment showing current progress, shown in a color defined by the color prop.
 *  2. A placeholder segment showing the remaining progress. The current progress overlaps this segment which remains
 * on the background.
 *
 * The current progress segment rotates clockwise when the spin prop is true, until the task completes,
 * After that a task completed state is shown with a video.
 */
const ProgressSpinner = ({
  progress,
  total,
  progressColor,
  remainingColor,
  backgroundColor,
  strokeWidth,
}: Props) => {
  const progressRatio = progress / total
  const progressRad = progressRatio * 2 * Math.PI

  const renderArc = ({
    endAngle,
    color,
    radius,
  }: {
    endAngle: number
    color: string
    radius: number
  }) => (
    <Arc
      data={{}}
      outerRadius={radius}
      innerRadius={radius - strokeWidth}
      cornerRadius={strokeWidth}
      startAngle={0}
      endAngle={endAngle}
      fill={color}
    />
  )

  return (
    <ParentSize>
      {({ width, height }) => {
        const centerY = height / 2
        const centerX = width / 2
        const radius = Math.min(width, height) / 2

        return (
          <div
            className={cx('relative', {
              'animate-spin': progress < total,
              'animate-pulse': progress >= total,
            })}
          >
            <svg width={width} height={height}>
              <rect
                rx={14}
                width={width}
                height={height}
                fill={backgroundColor}
              />
              <Group top={centerY} left={centerX}>
                {renderArc({
                  endAngle: 2 * Math.PI,
                  color: remainingColor,
                  radius,
                })}
                {renderArc({
                  endAngle: progressRad,
                  color: progressColor,
                  radius,
                })}
              </Group>
            </svg>
          </div>
        )
      }}
    </ParentSize>
  )
}

ProgressSpinner.defaultProps = {
  progressColor: tailwindConfig.theme.colors.primary.DEFAULT,
  remainingColor: tailwindConfig.theme.colors.primary[200],
  strokeWidth: 8,
  backgroundColor: 'transparent',
}

export default ProgressSpinner
